import { Injectable, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TransferState, makeStateKey } from '@angular/platform-browser';

const GCLID_KEY = makeStateKey<string>('gclid');

@Injectable({
  providedIn: 'root'
})
export class GclidService {
  private gclid: string | null = null;
  private readonly STORAGE_KEY = 'gclid';

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private transferState: TransferState,
    @Optional() @Inject('GCLID') private serverGclid?: string | null
  ) {
    if (isPlatformBrowser(this.platformId)) {
      // Try to get from TransferState first (SSR)
      const transferGclid = this.transferState.get<string | null>(GCLID_KEY, null);
      if (transferGclid) {
        this.gclid = transferGclid;
        this.saveToStorage(transferGclid);
      } else {
        // Try to get from localStorage
        this.gclid = this.getFromStorage();
      }
    } else {
      // Server-side: get from provider or TransferState
      if (this.serverGclid) {
        this.gclid = this.serverGclid;
        this.transferState.set(GCLID_KEY, this.serverGclid);
      } else {
        this.gclid = this.transferState.get<string | null>(GCLID_KEY, null);
      }
    }
  }

  captureFromUrl(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const gclidParam = urlParams.get('gclid');
    
    if (gclidParam) {
      this.gclid = gclidParam;
      this.saveToStorage(gclidParam);
      this.transferState.set(GCLID_KEY, gclidParam);
    }
  }

  get(): string | null {
    return this.gclid;
  }

  appendToUrl(partnerUrl: string): string {
    if (!this.gclid) {
      return partnerUrl;
    }

    try {
      const url = new URL(partnerUrl);
      // Append raw gclid value
      url.searchParams.set('gclid', this.gclid);
      return url.toString();
    } catch (e) {
      // If URL parsing fails, append manually
      const separator = partnerUrl.includes('?') ? '&' : '?';
      return `${partnerUrl}${separator}gclid=${encodeURIComponent(this.gclid)}`;
    }
  }

  private saveToStorage(gclid: string): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(this.STORAGE_KEY, gclid);
      } catch (e) {
        console.warn('Failed to save gclid to localStorage', e);
      }
    }
  }

  private getFromStorage(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      try {
        return localStorage.getItem(this.STORAGE_KEY);
      } catch (e) {
        console.warn('Failed to get gclid from localStorage', e);
        return null;
      }
    }
    return null;
  }
}

